import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import { markRaw } from 'vue';
import EditorEmbed from '../Components/EditorEmbed.vue';
import { blockRhythm, blockFrame, blockEmbed, blockFlush, embedIframe } from '../Themes/EditorClasses';

const defaultAspectRatio = 16 / 9;

function convertIframeElement(domNode) {
    const src = domNode.getAttribute('src');

    if (!src) return null;

    const aspectRatio = parseFloat(domNode.getAttribute('data-aspect-ratio')) || defaultAspectRatio;

    return { node: $createEmbedNode({ src, aspectRatio }) };
}

export class EmbedNode extends DecoratorNode {
    __src;
    __aspectRatio;
    __spacing;

    static getType() {
        return 'embed';
    }

    static clone(node) {
        return new EmbedNode(node.__src, node.__aspectRatio, node.__spacing, node.__key);
    }

    constructor(src, aspectRatio, spacing, key) {
        super(key);
        this.__src = src;
        this.__aspectRatio = aspectRatio || defaultAspectRatio;
        this.__spacing = spacing !== false;
    }

    static importJSON(serializedNode) {
        const { src, aspectRatio, spacing } = serializedNode;
        return $createEmbedNode({ src, aspectRatio, spacing });
    }

    exportJSON() {
        return {
            type: 'embed',
            version: 1,
            src: this.__src,
            aspectRatio: this.__aspectRatio,
            spacing: this.__spacing,
        };
    }

    static importDOM() {
        return {
            iframe: () => ({
                conversion: convertIframeElement,
                priority: 0,
            }),
        };
    }

    exportDOM() {
        // Mirror EditorEmbed.vue: block owns aspect-ratio inline so any ratio renders identically editor + public.
        const container = document.createElement('div');
        container.className = `${blockRhythm} ${blockFrame} ${blockEmbed}`;
        if (!this.__spacing) {
            container.classList.add(blockFlush);
        }
        container.style.aspectRatio = String(this.__aspectRatio);

        const iframe = document.createElement('iframe');
        iframe.setAttribute('src', this.__src);
        iframe.setAttribute('class', embedIframe);
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allowfullscreen', 'true');
        iframe.setAttribute('loading', 'lazy');
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');

        container.appendChild(iframe);
        return { element: container };
    }

    isInline() {
        return false;
    }

    createDOM() {
        return document.createElement('div');
    }

    updateDOM(prevNode) {
        return prevNode.__src !== this.__src
            || prevNode.__spacing !== this.__spacing;
    }

    decorate(editor) {
        return {
            component: markRaw(EditorEmbed),
            props: {
                src: this.__src,
                aspectRatio: this.__aspectRatio,
                spacing: this.__spacing,
                editable: editor.isEditable(),
            },
        };
    }

    updateData({ src, spacing }) {
        const writable = this.getWritable();
        writable.__src = src;
        writable.__spacing = spacing !== false;
    }
}

export function $createEmbedNode({ src, aspectRatio, spacing }) {
    return $applyNodeReplacement(new EmbedNode(src, aspectRatio || defaultAspectRatio, spacing));
}

export function $isEmbedNode(node) {
    return node instanceof EmbedNode;
}
